import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// types
import Task from '../../interfaces/Task.interface';
import MiniTask from '../../interfaces/MiniTask.interface';

interface TasksState {
  normalizedTasks: Task[];
}

const initialState: TasksState = {
  normalizedTasks: [],
};

function sortTasks(taskA: Task, taskB: Task) {
  let a = taskA.dueDate ? new Date(taskA.dueDate).getTime() : 3000000000000;
  let b = taskB.dueDate ? new Date(taskB.dueDate).getTime() : 3000000000000;
  return a - b;
}

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setTasks: (state, action: PayloadAction<Task[]>) => {
      state.normalizedTasks = action.payload;
    },
    createTask: (state, action: PayloadAction<Task>) => {
      state.normalizedTasks.push(action.payload);
      if (action.payload.parentTaskId) {
        const parentIndex = state.normalizedTasks.findIndex((task) => task.id === action.payload.parentTaskId);
        state.normalizedTasks[parentIndex].subtaskIds.push(action.payload.id);
      }
      state.normalizedTasks.sort(sortTasks);
    },
    updateTask: (state, action: PayloadAction<Task>) => {
      state.normalizedTasks = state.normalizedTasks
        .map((task) =>
          task.id === action.payload.id ? action.payload : task
        )
        .sort(sortTasks);
    },
    updateTaskMiniTask: (state, action: PayloadAction<MiniTask>) => {
      state.normalizedTasks = state.normalizedTasks
        .map((task: Task): Task => {
          if (task.id === action.payload.parentTaskId) {
            const currentTaskMiniTasks = task.miniTasks;
            const updatedMiniTasks = currentTaskMiniTasks.map((miniTask: MiniTask) =>
              miniTask.id === action.payload.id ? action.payload : miniTask
            );

            return {
              ...task,
              miniTasks: updatedMiniTasks
            };
          } else {
            return task;
          }
        })
        .sort(sortTasks);
    },
    deleteTaskMiniTask: (state, action: PayloadAction<number>) => {
      state.normalizedTasks = state.normalizedTasks
        .map((task: Task): Task => {
          const targetMiniTaskIndex = task.miniTasks.findIndex((miniTask: MiniTask) => {
            if (miniTask.id === action.payload) { return true; }
            else { return false }
          });

          if (targetMiniTaskIndex > -1) {
            const currentTaskMiniTasks = [...task.miniTasks];
            currentTaskMiniTasks.splice(targetMiniTaskIndex, 1);

            return {
              ...task,
              miniTasks: currentTaskMiniTasks
            };
          } else {
            return task;
          }
        })
        .sort(sortTasks);
    },
    createTaskMiniTask: (state, action: PayloadAction<MiniTask>) => {
      state.normalizedTasks = state.normalizedTasks
        .map((task: Task): Task => {
          if (task.id === action.payload.parentTaskId) {
            const currentTaskMiniTasks = task.miniTasks;

            return {
              ...task,
              miniTasks: [...currentTaskMiniTasks, action.payload]
            };
          } else {
            return task;
          }
        })
        .sort(sortTasks);
    },
    deleteTask: (state, action: PayloadAction<number>) => {
      state.normalizedTasks = state.normalizedTasks.filter((task) => task.id !== action.payload);
    },
    clearExcuse(state, action: PayloadAction<number>) {
      state.normalizedTasks = state.normalizedTasks
        .map((task) =>
          task.id === action.payload ? {
            ...task,
            excused: false,
            excuseExpiresAt: null,
          } : task
        );
    },
  },
});

export const {
  setTasks,
  createTask,
  updateTask,
  deleteTask,
  clearExcuse,
  updateTaskMiniTask,
  deleteTaskMiniTask,
  createTaskMiniTask
} = tasksSlice.actions;

export default tasksSlice.reducer;
